import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_CONFIG } from '../config/api';

// Custom base query that handles both JSON and text responses
const customBaseQuery = async (args, api, extraOptions) => {
  const result = await fetchBaseQuery({
    baseUrl: API_CONFIG.baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      if (endpoint !== 'getAccessToken') {
        const idpDetails = localStorage.getItem('idp_details');
        headers.set('x-request-source', 'web-ui');
        if (idpDetails) {
          headers.set('keycloak-token', 'true');
          if(JSON.parse(idpDetails).client_name) {
            headers.set('client-name', JSON.parse(idpDetails).client_name);
          }
        }
        
        // Priority: Basic cred first, then Bearer token, then localStorage token
      
        // Check if Authorization header is already set in args
        const existingAuthHeader = args?.headers?.Authorization;
        const isBasicAuth = existingAuthHeader?.startsWith('Basic ');
        const isBearerAuth = existingAuthHeader?.startsWith('Bearer ');
      
        if (existingAuthHeader && (isBasicAuth )) {
          // Use the provided token (Basic or Bearer) - highest priority
          headers.set('Authorization', existingAuthHeader);
          // Remove 'keycloak-token' from headers if it exists
          if (headers.has('keycloak-token')) {
            headers.delete('keycloak-token');
          }
          if (headers.has('client-name')) {
            headers.delete('client-name');
          }
        }
        else if(isBearerAuth){
           // Use the provided token (Basic or Bearer) - highest priority
           headers.set('Authorization', existingAuthHeader);
        } else {
          // Fallback to localStorage token for authenticated routes
          const authToken = localStorage.getItem('auth_token');
          if (authToken) {
            headers.set('Authorization', `Bearer ${authToken}`);
          }
        }
      }
      return headers;
    }
  })(args, api, extraOptions);

  // Handle text responses for specific endpoints
  if (result.data && typeof result.data === 'string' && args.url?.includes('/sendpubliclink')) {
    return {
      ...result,
      data: { message: result.data, success: true }
    };
  }
  
  // Handle parsing errors for sendpubliclink endpoint
  if (result.error && result.error.status === 'PARSING_ERROR' && args.url?.includes('/sendpubliclink')) {
    // If the API returns "OK" as plain text, treat it as success
    if (result.error.data === 'OK') {
      return {
        ...result,
        data: { message: 'OK', success: true },
        error: undefined
      };
    }
  }

  return result;
};

export const api = createApi({
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getMilestones: builder.query({
      query: (payload) => {
        const headers = {};
        if (payload.token) {
          if (payload.token.startsWith('Basic ')) {
            headers.Authorization = payload.token;
          } else {
            headers.Authorization = `Bearer ${payload.token}`;
          }
        }
        return {
          url: `/status/milestones/${payload.tripId || payload}`,
          method: 'GET',
          headers
        };
      },
    }),
    getOptimizedStatus: builder.query({
      query: (payload) => {
        // console.log('getOptimizedStatus payload:', payload);
        let trip_id = payload?.tripId ?? payload
        return {
          url: `/status/optimized/${trip_id}`,
          method: 'GET',
          headers: payload?.headers || {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        };
      },
    }),
    getMasterData: builder.query({
      query: (payload) => {
        // console.log('*************** getMasterData payload: **********', payload);
        return {
          url: `/masterdata/fetch?key=${payload?.key}`,
          method: 'GET',
          headers: payload?.headers || {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        };
      },
    }),
    getTrips: builder.query({
      query: (payload, queryParameters = '') => {
        const headers = {};
        // Add addConsents=true if addConsentsFromHome flag is set in payload
        let url = '/trips/fetch';
        if (payload.addConsentsFromHome) {
          url += '?addConsents=true';
        } else if (queryParameters || payload.queryParams) {
          queryParameters = payload.queryParams || queryParameters;
          url += queryParameters;
        }
        if (payload.token) {
          if (payload.token.startsWith('Basic ')) {
            headers.Authorization = payload.token;
            // delete payload.token;
            payload = { tripId: payload.payload?.tripId }
          } else {
            headers.Authorization = `Bearer ${payload.token}`;
          }
        }
        // console.log("******************* payload *******************", payload)
        return {
          url,
          method: 'POST',
          body: payload,
          headers
        };
      },
    }),
    login: builder.mutation({
      query: (credentials) => {
        const authHeader = btoa(`${credentials.username}:${credentials.password}`);
        return {
          url: '/login',
          method: 'POST',
          body: credentials,
          headers: {
            'Authorization': `Basic ${authHeader}`
          }
        };
      },
    }),
    getIdpDetails: builder.query({
      query: (username) => ({
        url: `/getIdPDetails?username=${encodeURIComponent(username)}`,
        method: 'GET'
      }),
    }),
    getAccessToken: builder.mutation({
      query: ({ body }) => {
        const formData = new URLSearchParams();
        Object.entries(body).forEach(([key, value]) => {
          formData.append(key, value);
        });

        return {
          url: 'https://auth.intusystems.info/auth/realms/TracknTrace/protocol/openid-connect/token',
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
        };
      },
    }),
    decodeToken: builder.mutation({
      query: (tokenData) => {
        const headers = {};
        if (tokenData.token.startsWith('Basic ')) {
          headers.Authorization = tokenData.token;
        } else {
          headers.Authorization = `Bearer ${tokenData.token}`;
        }
        if(tokenData.keycloak) {
          headers['keycloak-token'] = 'true';
        }
       
        return {
          url: '/decodetoken',
          method: 'GET',
          headers
        };
      }
    }),
    validateOtp: builder.mutation({
      query: ({ otp, token }) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        body: { otp },
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    }),
    validateToken: builder.mutation({
      query: ({ token }) => ({
        url: '/validatetoken',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    }),
    generateOtp: builder.mutation({
      query: ({ token }) => ({
        url: '/auth/otp/generate',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    }),
    forgotPassword: builder.mutation({
      query: (credentials) => ({
        url: '/users/forgot-password',
        method: 'POST',
        body: credentials
      }),
    }),
    verifyForgotPasswordOtp: builder.mutation({
      query: (data) => ({
        url: '/users/verify-otp',
        method: 'POST',
        body: data
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: '/users/reset-password',
        method: 'POST',
        body: data
      }),
    }),
    fetchStats: builder.query({
      query: (data) => ({
        url: '/trips/fetch-stats',
        method: 'POST',
        body: data
      }),
    }),
    getFilters: builder.query({
      query: () => ({
        url: '/trips/filters',
        method: 'GET'
      })
    }),
    getDevices: builder.query({
      query: () => ({
        url: '/devices',
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
    }),
    getTripRoutes: builder.query({
      query: (payload) => ({
        url: `/trips/fetch_routes_v2?trip_id=${payload?.tripId}`,
        method: 'GET',
        headers: payload?.headers || {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
    }),
    getPublicTripData: builder.query({
      query: ({ basicCred, tripId }) => ({
        url: `/public/trip/${tripId}`,
        method: 'GET',
        headers: {
          'Authorization': `Basic ${basicCred}`
        }
      })
    }),
    getFeatureList: builder.query({
      query: (token) => ({
        url: '/feature_list',
        method: 'GET',
        headers: {
          'Authorization': token?.startsWith('Basic ') ? token : `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
      transformResponse: (response) => {
        return response;
      },
      providesTags: ['FeatureList']
    }),
    getStatus: builder.query({
      query: ({ tripId, token }) => {
        const headers = {};
        if (token) {
          if (token.startsWith('Basic ')) {
            headers.Authorization = token;
          } else {
            headers.Authorization = `Bearer ${token}`;
          }
        }
        return {
          url: `/status?tripId=${tripId}`,
          method: 'GET',
          headers
        };
      },
    }),
    getTripChecklist: builder.query({
      query: (tripId) => ({
        url: `/trip-checklist/fetch/${tripId}`,
        method: 'GET',
      }),
    }),
    getTripChecklistList: builder.query({
      query: () => ({
        url: '/trip-checklist',
        method: 'GET',
      }),
    }),
    submitTripChecklist: builder.mutation({
      query: ({ tripId, checklistData }) => ({
        url: `/trip-checklist/${tripId}`,
        method: 'POST',
        body: checklistData,
      }),
    }),
    getCallLogs: builder.query({
      query: (tel) => ({
        url: `/consents/call_logs/${tel}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
    }),
    // Trigger consent for a given telephone number via missed call flow
    triggerConsent: builder.query({
      query: ({ tel, missed_call = true }) => ({
        url: `/consents?tel=${encodeURIComponent(tel)}&missed_call=${missed_call ? 'true' : 'false'}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
    }),
    getBanner: builder.query({
      query: () => ({
        url: '/banner',
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    sendPublicLink: builder.mutation({
      query: (payload) => ({
        url: '/trips/sendpubliclink',
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    getReports: builder.query({
      query: () => ({
        url: '/reports',
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    // DL details - fetch by DL number and DOB (YYYY-MM-DD)
    getDlDetails: builder.query({
      query: ({ dlnumber, dob }) => ({
        url: `/trips/saarthi/dl/get?dlnumber=${encodeURIComponent(dlnumber)}&dob=${encodeURIComponent(dob)}`,
        method: 'GET',
      }),
    }),
    // E-Challan details - fetch by vehicle number
    getChallanDetails: builder.query({
      query: (vehicleNumber) => ({
        url: `/trips/echallan/get?truck_number=${vehicleNumber}`,
        method: 'GET',
      }),
    }),
    // Vahan details - RC fetch by vehicle number
    getVahanTruckDetails: builder.query({
      query: (vehicleNumber) => ({
        url: `/trips/vaahan/truck/get/${vehicleNumber}`,
        method: 'GET',
      }),
    }),
    // FASTag details - fetch by vehicle number
    getFastagDetails: builder.query({
      query: (vehicleNumber) => ({
        url: `/trips/fasttag/get?truck_number=${vehicleNumber}`,
        method: 'GET',
      }),
    }),
    // Seal details for a trip
    getTripSeals: builder.query({
      query: (tripId) => ({
        url: `/trips/seals/${tripId}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    requestReport: builder.mutation({
      query: (payload) => ({
        url: '/reports/request',
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    getCustomReport: builder.mutation({
      query: (payload) => ({
        url: payload.endpoint,
        method: 'POST',
        body: payload.reportPayload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    startTrip: builder.mutation({
      query: (payload) => ({
        url: '/trips/start',
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    endTrip: builder.mutation({
      query: ({ tripId, payload }) => ({
        url: `/trips/end/${tripId}`,
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    // Geocode address/pincode to coordinates
    geocodeAddress: builder.mutation({
      query: (payload) => ({
        url: '/status/geocode',
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    // Manual update of trip location
    updateTripLocation: builder.mutation({
      query: ({ tripId, payload }) => ({
        url: `/status/manual_update/${tripId}`,
        method: 'PUT',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    // App status update for a trip
    appStatusUpdate: builder.mutation({
      query: (payload) => ({
        url: '/trips/app_status_update',
        method: 'POST',
        body: payload,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }),
    }),
    uploadDocument: builder.mutation({
      query: (formData) => ({
        url: '/documents/upload',
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })
    }),
    generateEpodToken: builder.mutation({
      query: ({ token, epod_stuff }) => ({
        url: 'https://epod.api.intusystems.info/api/generate-token-with-filters',
        method: 'POST',
        body: epod_stuff?.data ?? {},
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
    }),
  }),
});

export const { 
  useLoginMutation,
  useGetIdpDetailsQuery,
  useGetFiltersQuery,
  useGetDevicesQuery,
  useLazyGetDevicesQuery,
  useGetAccessTokenMutation,
  useDecodeTokenMutation,
  useValidateOtpMutation,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  useValidateTokenMutation,
  useGenerateOtpMutation,
  useForgotPasswordMutation,
  useVerifyForgotPasswordOtpMutation,
  useResetPasswordMutation,
  useFetchStatsQuery,
  useGetMilestonesQuery,
  useGetOptimizedStatusQuery,
  useGetTripRoutesQuery,
  useGetMasterDataQuery,
  useGetPublicTripDataQuery,
  useGetFeatureListQuery,
  useLazyGetFeatureListQuery,
  useGetStatusQuery,
  useLazyGetStatusQuery,
  useGetTripChecklistQuery,
  useGetTripChecklistListQuery,
  useSubmitTripChecklistMutation,
  useGetCallLogsQuery,
  useLazyGetCallLogsQuery,
  useTriggerConsentQuery,
  useLazyTriggerConsentQuery,
  useGetBannerQuery,
  useSendPublicLinkMutation,
  useGetReportsQuery,
  useRequestReportMutation,
  useGetDlDetailsQuery,
  useLazyGetDlDetailsQuery,
  useGetChallanDetailsQuery,
  useLazyGetChallanDetailsQuery,
  useGetVahanTruckDetailsQuery,
  useLazyGetVahanTruckDetailsQuery,
  useGetFastagDetailsQuery,
  useLazyGetFastagDetailsQuery,
  useGetTripSealsQuery,
  useGetCustomReportMutation,
  useStartTripMutation,
  useEndTripMutation,
  useGeocodeAddressMutation,
  useUpdateTripLocationMutation,
  useAppStatusUpdateMutation,
  useUploadDocumentMutation,
  useGenerateEpodTokenMutation,
} = api; 